import {
  AccountsService,
  CouponOfferStatus,
  GetCouponsResponse,
} from 'base/accounts_service';
import { AnalyticsService, CouponBannerImpressionEvent } from 'base/analytics';
import { Button } from 'base/ui/button';
import { Rows } from 'base/ui/layout';
import { TextRegular, TextSmall, TextSmallLight } from 'base/ui/typography';
import React from 'react';
import couponGraphic from './coupon-graphic.file.svg';
import styles from './coupon_banners.css';
import { manageAccountRouteToUrl } from './routes';

export function CouponBanners({
  accountsService,
  analytics,
  analyticsLocation,
}: {
  accountsService: AccountsService;
  analytics: AnalyticsService;
  analyticsLocation: CouponBannerImpressionEvent['location'];
}) {
  const [res, setRes] = React.useState<GetCouponsResponse | undefined>();

  React.useEffect(
    () => {
      accountsService
        .getCoupons()
        .then((res) => {
          setRes(res);
          if (res.offers.length > 0) {
            analytics.track({
              event: 'coupons/banner-impression',
              location: analyticsLocation,
              couponCodes: res.offers.map((o) => o.couponCode),
            });
          }
        })
        .catch((e) => {
          console.warn('[CouponBanners] Could not get coupons', e);
        });
    }, // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountsService],
  );

  if (res?.offers.length === 0) return null;
  return (
    <Rows spacing={16}>
      {res?.offers.map((coupon, i) => (
        <CouponBanner
          key={i}
          coupon={coupon}
          onClick={() => {
            analytics.track({
              event: 'coupons/banner-click',
              location: analyticsLocation,
              couponCode: coupon.couponCode,
            });
          }}
        />
      ))}
    </Rows>
  );
}

export function CouponBanner({
  coupon,
  onClick,
}: {
  coupon: CouponOfferStatus;
  onClick?: () => void;
}) {
  const {
    daysLeftToClaim: days,
    couponCode,
    planKey,
    discountPercent,
  } = coupon;

  let title;
  if (coupon.bannerUiName === 'mty-2month') {
    title = `Your loyalty deserves a reward. Here’s ${discountPercent}% off a yearly subscription ✨`;
  } else if (coupon.bannerUiName === 'mty-2month-backfill') {
    title = `Your loyalty deserves a reward. Here’s ${discountPercent}% off a yearly subscription ✨`;
  } else if (coupon.bannerUiName === 'flux-student-to-yearly') {
    title = `Unlock your exclusive Flux Student discount: ${discountPercent}% off a yearly subscription ✨`;
  } else if (coupon.bannerUiName === 'flux-student-to-monthly') {
    title = `Unlock your exclusive Flux Student discount: ${discountPercent}% off a monthly subscription ✨`;
  } else {
    console.warn(
      '[CouponBanner] Unknown bannerUiName - hiding the coupon',
      coupon,
    );
    return null;
  }

  return (
    <div className={styles.banner}>
      <img
        src={couponGraphic}
        alt=""
        role="presentation"
        className={styles.image}
      />
      <TextRegular>{title}</TextRegular>
      <div className={styles.actions}>
        <TextSmallLight color="grey">
          <TextSmall color="black" tag="span">
            {days === 0 ? 'Last day' : days === 1 ? '1 day' : `${days} days`}
          </TextSmall>
          {days === 0 ? ' to claim' : ' left to claim'}
        </TextSmallLight>
        <Button
          variant="tertiary"
          background="grey2"
          href={manageAccountRouteToUrl({
            path: 'manage-account/plan',
            dialog: { planKey, couponCode },
          })}
          onClick={onClick}
          label="Claim your Reward"
        />
      </div>
    </div>
  );
}
